/**
 * The terminology editor — T4.7 (client), T4.7a (blueprint) and T4.7b (state
 * labels). One component for both levels, because the rules are the same and
 * two copies would drift.
 *
 * What it shows, and why:
 *
 *   - Every field's placeholder AND a caption state what an EMPTY field falls
 *     back to, and where from ("system default" or "client"). Without that a
 *     person cannot tell "inherit" from "not set", and an empty field never
 *     blanks a label — it inherits (design.md § 2.1).
 *   - Singular and plural are separate fields, both stored, never derived. If
 *     only one of a pair is set the other still inherits, and the editor says
 *     what the pair will now read as, since "Workstream" beside "Rooms" is
 *     almost never what was meant. That is a warning, not an error: the API
 *     accepts it.
 *   - State labels are checked against the same rule the API applies, and a
 *     rejected label says WHY: a state label may not equal another state's
 *     standard name, nor another state's label, or a workbook's State column
 *     becomes ambiguous on import (design.md § 2.1).
 *
 * The names in the left-hand column come from DEFAULT_TERMINOLOGY — they are
 * the standard vocabulary that identifies which term a row overrides — not
 * literals in this file. The form's own prose avoids the nine terms entirely.
 *
 * Reads the stored OVERRIDE (`initial`), writes the stored override. It never
 * reads or writes the resolved terms; that is what the rest of the interface
 * consumes.
 */
import { useMemo, useState, type FormEvent } from 'react';
import {
  DEFAULT_TERMINOLOGY,
  NOUN_KEYS,
  STATE_TERM_KEYS,
  terminologyOverrideSchema,
  type NounKey,
  type StateTermKey,
  type Terminology,
  type TerminologyOverride,
} from '@blueprint/shared';
import { FormError } from './ui';

export type InheritSource = 'system default' | 'client';

type FieldId =
  | `noun.${NounKey}.singular`
  | `noun.${NounKey}.plural`
  | `state.${StateTermKey}`;

const LIMIT = 40;

function fieldsOf(override: TerminologyOverride): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of NOUN_KEYS) {
    const n = override.nouns?.[k];
    if (n?.singular) out[`noun.${k}.singular`] = n.singular;
    if (n?.plural) out[`noun.${k}.plural`] = n.plural;
  }
  for (const k of STATE_TERM_KEYS) {
    const s = override.states?.[k]?.label;
    if (s) out[`state.${k}`] = s;
  }
  return out;
}

/** Trimmed, non-empty values only — an empty field is absent, which inherits. */
function buildOverride(values: Record<string, string>): TerminologyOverride {
  const nouns: NonNullable<TerminologyOverride['nouns']> = {};
  for (const k of NOUN_KEYS) {
    const singular = values[`noun.${k}.singular`]?.trim();
    const plural = values[`noun.${k}.plural`]?.trim();
    if (singular || plural) {
      nouns[k] = { ...(singular ? { singular } : {}), ...(plural ? { plural } : {}) };
    }
  }
  const states: NonNullable<TerminologyOverride['states']> = {};
  for (const k of STATE_TERM_KEYS) {
    const label = values[`state.${k}`]?.trim();
    if (label) states[k] = { label };
  }
  return {
    ...(Object.keys(nouns).length > 0 ? { nouns } : {}),
    ...(Object.keys(states).length > 0 ? { states } : {}),
  };
}

/** The label a state will actually show once this override is applied. */
function effectiveStateLabel(k: StateTermKey, values: Record<string, string>, inherited: Terminology) {
  return values[`state.${k}`]?.trim() || inherited.states[k].label;
}

/** Shape problems (length, markup), from the very schema the API validates with. */
function shapeProblems(override: TerminologyOverride): Map<string, string> {
  const problems = new Map<string, string>();
  const parsed = terminologyOverrideSchema.safeParse(override);
  if (parsed.success) return problems;
  for (const issue of parsed.error.issues) {
    const [group, key, part] = issue.path;
    let id: string | null = null;
    if (group === 'nouns' && typeof key === 'string' && typeof part === 'string') id = `noun.${key}.${part}`;
    if (group === 'states' && typeof key === 'string') id = `state.${key}`;
    if (id && !problems.has(id)) problems.set(id, `This ${issue.message}.`);
  }
  return problems;
}

/**
 * State-label collisions, with the reason. Mirrors findStateLabelCollisions in
 * @blueprint/shared (which the API runs) but returns a message per FIELD, so the
 * explanation sits beside the input that caused it.
 */
function collisionProblems(values: Record<string, string>, inherited: Terminology): Map<string, string> {
  const problems = new Map<string, string>();
  const standard = STATE_TERM_KEYS.map((k) => DEFAULT_TERMINOLOGY.states[k].label);
  const seen = new Map<string, StateTermKey>();

  for (const k of STATE_TERM_KEYS) {
    const label = effectiveStateLabel(k, values, inherited);
    const norm = label.toLowerCase();
    const ownStandard = DEFAULT_TERMINOLOGY.states[k].label.toLowerCase();
    const id = `state.${k}`;

    const standardOwner = standard.find((s) => s.toLowerCase() === norm);
    if (standardOwner && norm !== ownStandard) {
      problems.set(
        id,
        `‘${label}’ is the standard name of the ${standardOwner} state. A workbook’s State column could not tell the two apart on import. Choose a different word.`,
      );
    }
    const earlier = seen.get(norm);
    if (earlier) {
      // Say the problem on the field the person can change. A label inherited
      // from the client is not editable here, so the message goes on the field
      // they set, and names the other as inherited.
      const isSet = (key: StateTermKey) => Boolean(values[`state.${key}`]?.trim());
      const pair: [StateTermKey, StateTermKey][] = [
        [k, earlier],
        [earlier, k],
      ];
      const editable = pair.filter(([self]) => isSet(self));
      for (const [self, other] of editable.length > 0 ? editable : pair) {
        const otherName = DEFAULT_TERMINOLOGY.states[other].label;
        problems.set(
          `state.${self}`,
          `‘${label}’ is also the label for the ${otherName} state${isSet(other) ? '' : ' (inherited)'}. Two states cannot share a label, or a workbook’s State column would be ambiguous on import.`,
        );
      }
    } else {
      seen.set(norm, k);
    }
  }
  return problems;
}

function Caption({ id, children }: { id: string; children: string }) {
  return (
    <span className="adm-help" id={id}>
      {children}
    </span>
  );
}

export function TerminologyEditor({
  inherited,
  sourceOf,
  initial,
  saving,
  saveError,
  savedNotice,
  onSave,
  levelName,
}: {
  /** What an empty field falls back to at this level. */
  inherited: Terminology;
  /** Where that fallback comes from, per field: the client's word or the system default. */
  sourceOf: (field: { noun: NounKey; part: 'singular' | 'plural' } | { state: StateTermKey }) => InheritSource;
  /** The stored override at THIS level. */
  initial: TerminologyOverride;
  saving: boolean;
  saveError: string | null;
  savedNotice: string | null;
  onSave: (override: TerminologyOverride) => void;
  /** Already-resolved words for the level being edited ("this client", "this workstream"). */
  levelName: string;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => fieldsOf(initial));

  const override = useMemo(() => buildOverride(values), [values]);
  const shape = useMemo(() => shapeProblems(override), [override]);
  const collisions = useMemo(() => collisionProblems(values, inherited), [values, inherited]);

  const stored = useMemo(() => JSON.stringify(buildOverride(fieldsOf(initial))), [initial]);
  const dirty = JSON.stringify(override) !== stored;
  const blocked = shape.size > 0 || collisions.size > 0;

  const set = (id: FieldId, value: string) => setValues((v) => ({ ...v, [id]: value }));
  const errorFor = (id: string) => shape.get(id) ?? collisions.get(id) ?? null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (saving || blocked || !dirty) return;
    onSave(override);
  };

  const overriddenCount = Object.keys(fieldsOf(override)).length;

  return (
    <form className="adm-terms" onSubmit={submit} noValidate>
      <p className="adm-lede">
        Change the words shown for {levelName}. Leave a field empty to inherit; an empty field never
        blanks a label. Each name is at most {LIMIT} characters of plain text. Nothing here changes
        a workbook: downloads always use the standard sheet, column and State names.
      </p>

      <h3 className="adm-h3">Nouns</h3>
      <div className="adm-terms-grid" role="group" aria-label="Noun overrides">
        <div className="adm-terms-head" aria-hidden="true">
          <span>Standard name</span>
          <span>Singular</span>
          <span>Plural</span>
        </div>
        {NOUN_KEYS.map((k) => {
          const std = DEFAULT_TERMINOLOGY.nouns[k];
          const sId = `noun.${k}.singular` as const;
          const pId = `noun.${k}.plural` as const;
          const sSet = Boolean(values[sId]?.trim());
          const pSet = Boolean(values[pId]?.trim());
          const inhS = inherited.nouns[k].singular;
          const inhP = inherited.nouns[k].plural;
          const srcS = sourceOf({ noun: k, part: 'singular' });
          const srcP = sourceOf({ noun: k, part: 'plural' });
          const pairWarning =
            sSet && !pSet
              ? `The plural is not set, so it will still read ‘${inhP}’ (${srcP}). Set it as well if the plural should change.`
              : !sSet && pSet
                ? `The singular is not set, so it will still read ‘${inhS}’ (${srcS}). Set it as well if the singular should change.`
                : null;
          return (
            <div className="adm-terms-row" key={k}>
              <span className="adm-terms-std">{std.singular}</span>
              {(['singular', 'plural'] as const).map((part) => {
                const id = part === 'singular' ? sId : pId;
                const inh = part === 'singular' ? inhS : inhP;
                const src = part === 'singular' ? srcS : srcP;
                const isSet = part === 'singular' ? sSet : pSet;
                const err = errorFor(id);
                return (
                  <div className="adm-terms-cell" key={part}>
                    <input
                      value={values[id] ?? ''}
                      placeholder={inh}
                      maxLength={LIMIT * 2}
                      autoComplete="off"
                      aria-label={`${std.singular}, ${part}`}
                      aria-invalid={err ? true : undefined}
                      aria-describedby={`${id}-cap${err ? ` ${id}-err` : ''}`}
                      onChange={(e) => set(id, e.target.value)}
                    />
                    <Caption id={`${id}-cap`}>
                      {isSet ? `Replaces ‘${inh}’ (${src})` : `Inherits ‘${inh}’ (${src})`}
                    </Caption>
                    {err ? (
                      <span className="adm-field-err" id={`${id}-err`} role="alert">
                        {err}
                      </span>
                    ) : null}
                  </div>
                );
              })}
              {pairWarning ? <p className="adm-warn">{pairWarning}</p> : null}
            </div>
          );
        })}
      </div>

      <h3 className="adm-h3">State labels</h3>
      <p className="adm-lede">
        A label changes the word only. The four states keep their order, their colours and their
        percentages. A label may not be another state’s standard name, and no two states may share
        one.
      </p>
      <div className="adm-terms-grid" role="group" aria-label="State label overrides">
        <div className="adm-terms-head adm-terms-head-states" aria-hidden="true">
          <span>Standard name</span>
          <span>Label</span>
        </div>
        {STATE_TERM_KEYS.map((k) => {
          const std = DEFAULT_TERMINOLOGY.states[k].label;
          const id = `state.${k}` as const;
          const inh = inherited.states[k].label;
          const src = sourceOf({ state: k });
          const isSet = Boolean(values[id]?.trim());
          const err = errorFor(id);
          return (
            <div className="adm-terms-row adm-terms-row-states" key={k}>
              <span className="adm-terms-std">{std}</span>
              <div className="adm-terms-cell">
                <input
                  value={values[id] ?? ''}
                  placeholder={inh}
                  maxLength={LIMIT * 2}
                  autoComplete="off"
                  aria-label={`${std}, label`}
                  aria-invalid={err ? true : undefined}
                  aria-describedby={`${id}-cap${err ? ` ${id}-err` : ''}`}
                  onChange={(e) => set(id, e.target.value)}
                />
                <Caption id={`${id}-cap`}>
                  {isSet ? `Replaces ‘${inh}’ (${src})` : `Inherits ‘${inh}’ (${src})`}
                </Caption>
                {err ? (
                  <span className="adm-field-err" id={`${id}-err`} role="alert">
                    {err}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {saveError ? <FormError>{saveError}</FormError> : null}
      {savedNotice && !dirty ? (
        <p className="status adm-notice" role="status">
          {savedNotice}
        </p>
      ) : null}

      <div className="adm-terms-actions">
        <button type="submit" className="btn primary" disabled={saving || blocked || !dirty}>
          {saving ? 'Saving…' : 'Save terminology'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={saving || overriddenCount === 0}
          onClick={() => setValues({})}
        >
          Clear every override
        </button>
        {blocked ? (
          <span className="adm-help">Fix the highlighted fields to save.</span>
        ) : !dirty ? (
          <span className="adm-help">No unsaved changes.</span>
        ) : null}
      </div>
    </form>
  );
}
